import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { AppNav } from "../../components/AppNav";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Layout } from "../../components/Layout";
import { Body, Display, Heading, Kicker, Muted } from "../../components/Typography";
import { UnreadBadge } from "../../components/UnreadBadge";
import { ApiError, api } from "../../lib/api";
import type { StoreResponse, UserResponse } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import styles from "./StoresList.module.css";

// Cap on simultaneous store setups (operator, 2026-08-24). UI-level guard — the
// wizard itself creates nothing until step 1 submits.
const MAX_PARALLEL_SETUPS = 3;

const greeting = (date: Date): string => {
  const h = date.getHours();
  if (h < 5) return "Up late";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const firstName = (user: UserResponse): string => {
  if (user.display_name) return user.display_name.split(/\s+/)[0] ?? user.display_name;
  const local = user.email.split("@")[0] ?? user.email;
  return local.charAt(0).toUpperCase() + local.slice(1);
};

const formatKicker = (date: Date): string => {
  const weekday = date.toLocaleDateString(undefined, { weekday: "long" });
  const month = date.toLocaleDateString(undefined, { month: "long" });
  const day = date.getDate();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${weekday} · ${month} ${day} · ${hours}:${minutes}`.toUpperCase();
};

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0] ?? "?").slice(0, 2).toUpperCase();
  // First two words — "Inman Square Grocery" -> "IS", matching the Paper mocks.
  return ((parts[0] ?? "").charAt(0) + (parts[1] ?? "").charAt(0)).toUpperCase();
};

const city = (address: string | null): string | null => {
  if (!address) return null;
  // Conventional "Street, City" — take the last comma-separated segment.
  const parts = address
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts[parts.length - 1] ?? null;
};

const roleFor = (store: StoreResponse, user: UserResponse): "Owner" | "Member" =>
  store.owner_id === user.id ? "Owner" : "Member";

const summaryCopy = (count: number): string => {
  if (count === 0) return "No stores yet — add your first below.";
  if (count === 1) return "One store reporting.";
  return `${count} stores reporting.`;
};

// What the backend DELETE actually cascades (stores FKs are ON DELETE CASCADE):
// edge_agents (and with them the edge API key), cameras, NVRs, members/grants, and
// the store's billing rows — the pending_billing subscription included.
const discardConfirmCopy = (label: string): string =>
  `Discard "${label}"? This deletes the store, its edge agent provisioning, any discovered cameras, and its billing record. The edge API key becomes invalid. This cannot be undone.`;

type StoreRowProps = {
  store: StoreResponse;
  role: "Owner" | "Member";
};

const StoreRow = ({ store, role }: StoreRowProps) => {
  const subCity = city(store.address);
  const closed = store.closed_at !== null;
  return (
    <Link to={`/stores/${store.id}`} className={styles.row} aria-label={store.name}>
      <div className={styles.cellStore}>
        <span aria-hidden="true" className={styles.storeBadge}>
          {initials(store.name)}
        </span>
        <div className={styles.storeMeta}>
          <div className={styles.storeName}>
            {store.name}
            {closed && (
              <Badge variant="neutral" className={styles.closedBadge}>
                Closed
              </Badge>
            )}
          </div>
          <span className={styles.storeSub}>
            {closed && store.closed_reason
              ? store.closed_reason
              : subCity
                ? `${subCity} · ${store.timezone}`
                : store.timezone}
          </span>
        </div>
      </div>
      <span className={styles.count}>
        {store.cameras_enabled ?? 0} / {store.cameras_total ?? 0}
      </span>
      <span className={styles.count}>{store.nvr_count ?? 0}</span>
      <UnreadBadge count={store.unread_alerts} />
      <Badge variant={role === "Owner" ? "accent" : "neutral"}>{role}</Badge>
      <span aria-hidden="true" className={styles.chev}>
        ›
      </span>
    </Link>
  );
};

export const StoresList = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState<StoreResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [now, setNow] = useState(() => new Date());
  const [discardingId, setDiscardingId] = useState<string | null>(null);

  // Clock tick for the kicker — once a minute is plenty.
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.listStores();
        if (!cancelled) setStores(list);
      } catch (e) {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? (e.body?.error ?? e.message)
            : "Couldn't load stores. Try reloading the page.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // A store only appears once its edge-agent key has been acknowledged (onboarding "commit").
  // Closed stores (closed_at set by the operator; history stays readable) leave the main
  // table for a collapsed section of their own.
  const matching = useMemo(() => {
    if (!stores) return null;
    const visible = stores.filter((s) => s.edge_key_acknowledged_at !== null);
    const q = query.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter(
      (s) => s.name.toLowerCase().includes(q) || (s.address ?? "").toLowerCase().includes(q),
    );
  }, [stores, query]);
  const filtered = useMemo(
    () => (matching ? matching.filter((s) => s.closed_at === null) : null),
    [matching],
  );
  const closedStores = useMemo(
    () => (matching ?? []).filter((s) => s.closed_at !== null),
    [matching],
  );

  // Every store whose setup isn't finished — drafts (key not yet acknowledged) and
  // acknowledged-but-unfinished alike. Up to MAX_PARALLEL_SETUPS may run at once
  // (operator, 2026-08-24); each gets its own stacked resume banner below.
  const inProgress = useMemo(
    () => (stores ?? []).filter((s) => s.setup_completed_at === null && s.closed_at === null),
    [stores],
  );
  const atSetupCap = inProgress.length >= MAX_PARALLEL_SETUPS;

  const addStore = useCallback(() => navigate("/stores/new"), [navigate]);

  // Draft stores never render a card, so Settings' Danger zone is unreachable for
  // them — the banner is their only delete surface (operator bug, 2026-08-26).
  const discardStore = useCallback(async (store: StoreResponse, label: string) => {
    if (!window.confirm(discardConfirmCopy(label))) return;
    setDiscardingId(store.id);
    setError(null);
    try {
      await api.deleteStore(store.id);
      setStores((prev) => (prev ? prev.filter((s) => s.id !== store.id) : prev));
    } catch (e) {
      setError(
        e instanceof ApiError
          ? (e.body?.error ?? e.message)
          : "Couldn't discard that store. Try again.",
      );
    } finally {
      setDiscardingId(null);
    }
  }, []);

  return (
    <Layout
      nav={
        <AppNav
          trail={[{ label: "Your stores" }]}
          onSignOut={logout}
          userName={user?.display_name ?? user?.email}
          userAvatar={user?.avatar ?? null}
        />
      }
    >
      <main className={styles.content}>
        <section className={styles.head}>
          <div className={styles.headLeft}>
            <Kicker>{formatKicker(now)}</Kicker>
            <Display>
              {user ? `${greeting(now)}, ${firstName(user)}.` : `${greeting(now)}.`}
            </Display>
            <Body>
              <Muted>
                {filtered === null
                  ? "Loading stores…"
                  : error
                    ? error
                    : summaryCopy(filtered.length)}
              </Muted>
            </Body>
          </div>
          <div className={styles.headRight}>
            <Input
              placeholder="Search stores"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              trailingAction={<span className={styles.kbd}>⌘K</span>}
            />
            <Button
              type="button"
              onClick={addStore}
              disabled={atSetupCap}
              title={
                atSetupCap
                  ? `Up to ${MAX_PARALLEL_SETUPS} stores can be set up at a time — finish one first.`
                  : undefined
              }
            >
              + Add a Store
            </Button>
          </div>
        </section>

        {filtered && filtered.length > 0 && (
          <section className={styles.table} aria-label="Stores">
            <div className={styles.tableHeader}>
              <span>Store</span>
              <span>Cameras</span>
              <span>NVRs</span>
              <span>Unread alerts</span>
              <span>Role</span>
              <span aria-hidden="true" />
            </div>
            {filtered.map((store) => (
              <StoreRow
                key={store.id}
                store={store}
                role={user ? roleFor(store, user) : "Member"}
              />
            ))}
          </section>
        )}

        {filtered && filtered.length === 0 && closedStores.length === 0 && !error && (
          <section className={styles.empty} aria-label="No stores">
            <Heading level={3}>
              {query ? "No stores match that search." : "Nothing here yet."}
            </Heading>
            <Body>
              <Muted>
                {query
                  ? "Try a shorter query, or clear the search."
                  : "Your first store is a button-press away."}
              </Muted>
            </Body>
          </section>
        )}

        {closedStores.length > 0 && (
          <details className={styles.closed} aria-label="Closed stores">
            <summary className={styles.closedSummary}>
              Closed stores ({closedStores.length})
            </summary>
            <div className={styles.table}>
              {closedStores.map((store) => (
                <StoreRow
                  key={store.id}
                  store={store}
                  role={user ? roleFor(store, user) : "Member"}
                />
              ))}
            </div>
          </details>
        )}

        {inProgress.map((store) => {
          const label = store.name.trim() || store.id.slice(0, 8);
          const acknowledged = store.edge_key_acknowledged_at !== null;
          const isOwner = user != null && store.owner_id === user.id;
          return (
            <section
              key={store.id}
              className={styles.ctaCard}
              aria-label={`Resume setting up ${label}`}
            >
              <div className={styles.ctaBody}>
                <span className={styles.ctaKicker}>
                  {acknowledged ? "Setup unfinished" : "Setup in progress"}
                </span>
                <Heading level={2}>Pick up where you left off — {label}.</Heading>
                <Body>
                  <Muted>
                    {acknowledged
                      ? "Once your edge agent has discovered the store's cameras, describe what each one sees to start monitoring. Your progress is saved."
                      : "Resume to provision this store's edge agent and finish setup. Your progress is saved."}
                  </Muted>
                </Body>
              </div>
              <div className={styles.ctaActions}>
                <Button onClick={() => navigate(`/stores/${store.id}/setup`)}>Resume setup</Button>
                {isOwner && (
                  <Button
                    variant="destructive"
                    onClick={() => discardStore(store, label)}
                    disabled={discardingId === store.id}
                  >
                    {discardingId === store.id ? "Discarding…" : "Discard"}
                  </Button>
                )}
              </div>
            </section>
          );
        })}

        {inProgress.length === 0 && (
          <section className={styles.ctaCard} aria-label="Add a store">
            <div className={styles.ctaBody}>
              <span className={styles.ctaKicker}>Opened a new shop?</span>
              <Heading level={2}>Add a store in under five minutes.</Heading>
              <Body>
                <Muted>
                  Give us the store details, connect your edge agent, and it auto-discovers every
                  camera on the network. The setup wizard walks you through it.
                </Muted>
              </Body>
            </div>
            <Button variant="outline" onClick={addStore}>
              Start setup
            </Button>
          </section>
        )}
      </main>
    </Layout>
  );
};
